import { format as isFormat, parse as isParse, isYesterday, isToday, isThisWeek, differenceInDays, formatDistanceToNow, isTomorrow, addMilliseconds, parseISO, formatISO, subMilliseconds, startOfDay, startOfWeek, startOfMonth, startOfYear, endOfDay, endOfWeek, endOfMonth, endOfYear, differenceInMilliseconds, differenceInSeconds, differenceInMinutes, differenceInHours, differenceInWeeks, differenceInMonths, differenceInQuarters, differenceInYears, eachDayOfInterval, eachWeekOfInterval, eachMonthOfInterval, eachYearOfInterval, getQuarter, addMinutes } from 'date-fns'
import { zonedTimeToUtc, utcToZonedTime, getTimezoneOffset } from 'date-fns-tz';
import sourceTimezones from 'timezones-list';
import locale from 'date-fns/locale/ru'

import { toggleCapital } from './common';
import { sortAsName } from './immutable';

export const parseAt = (string, { template = 'y-MM-dd HH:mm:ss', zoned = false } = {}) => string && [isParse(string, template, new Date())].map(x => zoned ? toZonedTime(x) : x)[0]

export const parse = (string, { template, withTime = true, iso = false } = {}) => string && (
  iso 
    ? parseISO(string) 
    : isParse(string, template || [
      'y-MM-dd',
      withTime && 'HH:mm:ss'
    ].filter(x => x).join(' '), new Date())
)

export const format = (date, { withDate = true, withTime = true, withSeconds = true } = {}) => isFormat(date, [
  withDate && 'y-MM-dd',
  withTime && [
    'HH',
    'mm',
    withSeconds && 'ss'
  ].filter(x => x).join(':')
].filter(x => x).join(' '))

export const formatAsDate = date => isFormat(date, 'y-MM-dd')
export const formatAsTime = date => isFormat(date, 'HH:mm:ss')

export const formatForServer = (date, { withTime = true, zoned = true, iso = false } = {}) => iso ? formatISO(date) : isFormat(zoned ? utcToZonedTime(date, 'UTC') : date, [
  'y-MM-dd',
  withTime && 'HH:mm:ss'
].filter(x => x).join(' '))

export const formatAsLabel = (date, { template, prefix, postfix, withDate = true, withTime = true, withAlias = true, withCapital = false, zoned = true } = {}) => {
  const at = zoned ? toZonedTime(date) : date

  const x = [
    withAlias && isYesterday(at) && toggleCapital('вчера', withCapital),
    withAlias && isToday(at) && toggleCapital('сегодня', withCapital),
    withAlias && isTomorrow(at) && toggleCapital('завтра', withCapital),
    withAlias && isThisWeek(at) && toggleCapital(isFormat(at, 'EEEE', { locale }), withCapital)
  ].find(x => x) || isFormat(at, 'dd MMMM y', { locale })

  const y = isFormat(at, 'HH:mm', { locale })

  const t = template && toggleCapital(isFormat(at, template, { locale }), withCapital)

  return t || [
    prefix,
    withDate && x,
    (withTime || postfix) && 'в',
    withTime && y,
    postfix
  ].filter(x => x).join(' ')
}

export const labelAt = (string, { prefix, postfix, withDate = true, withTime = true, withAlias = true, withCapital = false, iso = false, zoned = true } = {}) =>
  string && formatAsLabel(parse(string, { iso }), { prefix, postfix, withDate, withTime, withAlias, withCapital, zoned })

export const formatLabelWithDistance = (date, { withCapital } = {}) => {
  const dateToUtc = zonedTimeToUtc(date, 'UTC')
  const today = Date.now()
  const difference = differenceInDays(today, dateToUtc)

  return toggleCapital(difference < 7 ? `${formatDistanceToNow(dateToUtc, { locale })} назад` : isFormat(dateToUtc, 'dd.MM.yyyy'), withCapital)
}

export const timezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

export const timezones = () => sourceTimezones.sort(sortAsName(({ tzCode }) => tzCode))

export const timezonesEntries = () => timezones().map(({ tzCode, label }) => ({
  key: key(),
  value: tzCode,
  label
}))

export const toZonedTime = date => date && addMilliseconds(date, getTimezoneOffset(timezone(), date))

export const fromZonedTime = date => date && subMilliseconds(date, getTimezoneOffset(timezone(), date))


// FROM MOMENT
export const startOf = (date, type = 'month', format) => {
  const d = new Date(date)
  const _date = {
    [type === 'day']: startOfDay(d),
    [type === 'week']: startOfWeek(d),
    [type === 'month']: startOfMonth(d),
    [type === 'year']: startOfYear(d)
  }[true] || startOfMonth(d)
  
  return format ? isFormat(_date, format) : _date
}

export const endOf = (date, type = 'month', format) => {
  const d = new Date(date)
  const _date = {
    [type === 'day']: endOfDay(d),
    [type === 'week']: endOfWeek(d),
    [type === 'month']: endOfMonth(d),
    [type === 'year']: endOfYear(d)
  }[true] || startOfMonth(d)

  return format ? isFormat(_date, format) : _date
}

export const fromLocalToUtcEndOfDay = date => {
  return zonedTimeToUtc(endOfDay(date))
}

export const fromLocalToUtcStartOfDay = date => {
  return zonedTimeToUtc(startOfDay(date))
}

export const dateToLocal = (date, timezone = 'utc', format = 'y-MM-dd HH:mm:ss') => {
  return date && isFormat(utcToZonedTime(new Date(date), timezone), format, { locale })
}

export const getDatesDiff = (dates, type = 'years') => {
  const diff = {
    'milliseconds': differenceInMilliseconds,
    'seconds': differenceInSeconds,
    'minutes': differenceInMinutes,
    'hours': differenceInHours,
    'days': differenceInDays,
    'weeks': differenceInWeeks,
    'months': differenceInMonths,
    'quarters': differenceInQuarters,
    'years': differenceInYears
  }[type] || differenceInYears

  return diff(new Date(dates[1]), new Date(dates[0]))
}

export const getDatesRange = (from, to, type = 'years', format = 'yyyy') => {
  const start = new Date(from)
  const end = new Date(to)
  
  if (start > end) {
    return [] 
  }

  const intervalFunc = {
    'days': eachDayOfInterval,
    'weeks': (interval) => eachWeekOfInterval(interval, { weekStartsOn: 1 }),
    'months': eachMonthOfInterval,
    'years': eachYearOfInterval
  }[type] || eachYearOfInterval

  return intervalFunc({ start, end }).map(x => isFormat(x, format))
}

export const getQuarterDate = date => date && getQuarter(new Date(date)) < 3 ? 1 : 2

export const distanceToNow = date => {
  return formatDistanceToNow(new Date(date), { locale });
}

/**
 * TODO@solution
 * @param {string} value
 * @returns {Date}
 * @controversial
 */
export const toLocalDateFromISO = value => {
  return value && addMinutes(parseISO(value), Math.abs(new Date().getTimezoneOffset()))
}

/**
 * @param {Date} at
 * @param {string} template
 * @returns {string}
*/
export const toDateLabel = (at, template = 'dd.MM.yyyy') => ({
  [isToday(at)]: 'Сегодня',
  [isYesterday(at)]: 'Вчера'
})[true] || format(at, template)

/**
 * @param {Date} at
 * @param {string} template
 * @returns {string}
*/
export const toTimeLabel = (at, template = 'HH:mm') => format(at, template)

/**
 * TODO@place
 * @param {Date} at
 * @returns {string}
 */
export const toDateTimeLabel = at => {
  const date = at && toDateLabel(at)
  const time = at && toTimeLabel(at)
  return `${date || 'N/A'} в ${time || 'N/A'}`
}

export const toDateRangeForServer = ([from, to]) => {
  return [
    format(utcToZonedTime(from, 'UTC'), 'y-MM-dd HH:mm:ss'),
    format(utcToZonedTime(endOfDay(to), 'UTC'), 'y-MM-dd HH:mm:ss')
  ]
}

export const dateToRu = iso => {
  if (!iso) return null
  const date = new Date(iso)
  return `${date.getUTCDate()}/${date.getUTCMonth() + 1}/${date.getUTCFullYear() % 100}`
}

export const makeDatesRangeBetween = (dateFrom, dateTo) => {
  const datesRange = [];

  let comparableDateFrom = new Date(dateFrom);
  comparableDateFrom.setHours(0, 0, 0, 0);
  let comparableDateAfter = new Date(dateTo);
  comparableDateAfter.setHours(0, 0, 0, 0);

  // eslint-disable-next-line no-console
  while (comparableDateFrom.getTime() <= comparableDateAfter.getTime()) {
    let date = new Date(comparableDateFrom);
    datesRange.push(date);
    comparableDateFrom.setDate(comparableDateFrom.getDate() + 1);
  }

  return datesRange;
}

export const dateFormat = (date, timezone = 'utc', format = 'dd.MM.y HH:mm:ss') => {
  if (!date) {
    return 'Неизвестно';
  }
  return dateToLocal(date, timezone, format)
}